/**
 * Quel message le bandeau de connexion doit-il montrer ?
 *
 * Une seule ligne, un seul message à la fois, par ordre d'importance :
 *  1. « Hors ligne » ou « Serveur indisponible » — mais seulement après 1 s
 *     de coupure continue : une micro-coupure Wi-Fi ne doit pas faire
 *     clignoter l'écran ;
 *  2. « Connexion rétablie » — 3 s, et seulement si une coupure a vraiment été
 *     montrée (sinon on annoncerait le retour de quelque chose que personne
 *     n'a vu partir) ;
 *  3. « Connexion lente ».
 * La nouvelle version, elle, est signalée dans le pied de page (`UpdateNotice`).
 */
import { useEffect, useState } from "react";

import { useIsSlowNetwork } from "@/lib/api/connection-status";
import { useOfflinePendingCount } from "@/lib/api/offline-mutations";
import { useIsOnline } from "@/lib/api/online-manager";
import { useIsServerUnreachable } from "@/lib/api/server-reachability";

export const CUT_DISPLAY_DELAY_MS = 1_000;
export const RESTORED_DISPLAY_MS = 3_000;

export type ConnectionBannerState =
  | { kind: "offline"; pending: number }
  | { kind: "server-down"; pending: number }
  | { kind: "restored"; pending: number }
  | { kind: "slow" }
  | null;

export function useConnectionBanner(): ConnectionBannerState {
  const online = useIsOnline();
  const serverDown = useIsServerUnreachable();
  const slow = useIsSlowNetwork();
  const pending = useOfflinePendingCount();

  const [cutShown, setCutShown] = useState(false);
  const [restored, setRestored] = useState(false);

  // Coupure : montrée après 1 s continue ; au retour, « rétablie » la remplace.
  useEffect(() => {
    if (!online) {
      setRestored(false);
      if (cutShown) return undefined;
      const t = setTimeout(() => setCutShown(true), CUT_DISPLAY_DELAY_MS);
      return () => clearTimeout(t);
    }
    if (cutShown) {
      setCutShown(false);
      setRestored(true);
    }
    return undefined;
  }, [online, cutShown]);

  // « Rétablie » s'efface seule.
  useEffect(() => {
    if (!restored) return undefined;
    const t = setTimeout(() => setRestored(false), RESTORED_DISPLAY_MS);
    return () => clearTimeout(t);
  }, [restored]);

  if (!online) {
    if (!cutShown) return null;
    return serverDown ? { kind: "server-down", pending } : { kind: "offline", pending };
  }
  if (restored) return { kind: "restored", pending };
  if (slow) return { kind: "slow" };
  return null;
}
